import { useFormikContext } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { BucketFormikForm as Form } from 'Src/types/forms';
import { t } from 'Src/i18n';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import type {
  FormFieldOption,
  FormProducts,
  ProductOption,
  NewsletterOptins,
} from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import { useAutoScrollToError } from 'Components/frameworks/formik/ScrollToErrorHook';
import type { FormCivilities, FormValues } from './PrebookingForm.utils';
import { PrebookingFormTravelInformations } from './PrebookingFormTravelInformations';
import { PrebookingFormProject } from './PrebookingFormProject';
import { PrebookingFormSearchButton } from './PrebookingFormSearchButton';
import { EstimationCostProposal } from './EstimationCostProposal';
import { ActionsEstimationCostProposal } from './ActionsEstimationCostProposal';
import { ModifyButton } from './ModifyButton';
export type PrebookingFormBaseProps = {
  adultsOptions: Array<FormFieldOption>;
  agencies: Array<FormFieldOption>;
  civilities: FormCivilities;
  dateFormat: string;
  departureDateEnd: Date;
  departureDateStart: Date;
  hasUnicodeFields: boolean;
  hasUser: boolean;
  kidsOptions: Array<FormFieldOption>;
  consentOptins: NewsletterOptins;
  productOptions: Array<ProductOption>;
  products: FormProducts;
};
export type PrebookingFormProps = PrebookingFormBaseProps & {
  hasError: boolean;
  proposal: null | Proposal;
  setProposal: (proposal: Proposal | null) => void;
  setFormStep: (formStep: number) => void;
  setHasError: (hasError: boolean) => void;
  formStep: number;
};
export const PrebookingForm = ({
  adultsOptions,
  agencies,
  civilities,
  dateFormat,
  departureDateEnd,
  departureDateStart,
  formStep,
  hasError,
  hasUnicodeFields,
  hasUser,
  kidsOptions,
  consentOptins,
  productOptions,
  products,
  proposal,
  setProposal,
  setFormStep,
  setHasError,
}: PrebookingFormProps) => {
  const formik = useFormikContext<FormValues>();
  const { values, setFieldValue } = formik;
  const [fetchProposalError, setFetchProposalError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { productId } = values;
  const product = productId ? products[productId] : null;
  useAutoScrollToError(formik);
  useEffect(() => {
    const departureDate = values.departureDate.from;

    if (
      productId &&
      departureDate &&
      !products[productId]?.openingDates.some(({ opening, closing }) =>
        moment(departureDate).isBetween(momentFromApiDate(opening), momentFromApiDate(closing)),
      )
    ) {
      setFieldValue('departureDate', {
        from: null,
        to: null,
      });
    }
  }, [productId]);
  useEffect(() => {
    const { accommodationCategory, departureCity, duration } = values;

    if (product) {
      const accomodationInProduct = product.accommodations?.find(
        (accomodation) => accomodation.value === accommodationCategory,
      );
      const transportInProduct = product.transports?.find(
        (transport) => transport.value === departureCity,
      );
      const durationsInProduct = product.durations?.find((dur) => dur.value === duration);

      if (!accomodationInProduct) {
        const firstProductAccomodationCategory = product.accommodations[0].value;
        setFieldValue('accommodationCategory', firstProductAccomodationCategory);
      }

      if (!transportInProduct) {
        const firstProductTransport = product.transports[0].value;
        setFieldValue('departureCity', firstProductTransport);
      }

      if (!durationsInProduct) {
        const firstProductDuration = product.durations[0].value;
        setFieldValue('duration', firstProductDuration);
      }
    }
  }, [product]);
  useEffect(() => {
    if (agencies && agencies.length === 1) {
      setFieldValue('agency', agencies[0].value);
    }
  }, [agencies]);

  const resetProposal = () => {
    setHasError(false);
    setProposal(null);
    setFetchProposalError(null);
  };

  return (
    <Form id="prebooking-form-form" data-testid="prebooking-form-form">
      <PrebookingFormProject
        adultsOptions={adultsOptions}
        dateFormat={dateFormat}
        departureDateEnd={departureDateEnd}
        departureDateStart={departureDateStart}
        kidsOptions={kidsOptions}
        productOptions={productOptions}
        product={product}
        products={products}
        isDisabled={formStep > 0 || isLoading}
      />
      {isLoading && (
        <div className="pbf-loader mt-20 block w-full">
          <AsyncLoader />
          <p className="mt-20 text-center text-16">
            {t('prebookingForm.estimativePrice.loaderLabel')}
          </p>
        </div>
      )}
      {!isLoading && formStep === 0 && (
        <div className="my-10 flex w-full justify-center">
          <PrebookingFormSearchButton
            setProposal={setProposal}
            setIsLoading={setIsLoading}
            product={product}
            setFormStep={setFormStep}
            setFetchProposalError={setFetchProposalError}
          />
        </div>
      )}
      {formStep > 0 && (
        <div className="my-10 flex w-full justify-center">
          <ModifyButton
            hasPrice={!!proposal?.price}
            formStep={formStep}
            setFormStep={setFormStep}
            resetProposal={resetProposal}
          />
        </div>
      )}
      <div>
        {formStep >= 1 && (
          <EstimationCostProposal
            proposal={proposal}
            product={product}
            fetchProposalError={fetchProposalError}
            departureCities={product?.transports || []}
          />
        )}
        {formStep === 2 && (
          <PrebookingFormTravelInformations
            agencies={agencies}
            civilities={civilities}
            dateFormat={dateFormat}
            hasUnicodeFields={hasUnicodeFields}
            hasUser={hasUser}
            consentOptins={consentOptins}
          />
        )}
        {hasError && (
          <div className="mb-20 mt-40 w-full">
            <p className="text-center text-16 font-semibold text-red">
              {t('prebookingForm.submitError')}
            </p>
          </div>
        )}
        {formStep > 0 && (
          <ActionsEstimationCostProposal
            hasPrice={!!proposal?.price}
            formStep={formStep}
            setFormStep={setFormStep}
            resetProposal={resetProposal}
          />
        )}
      </div>
    </Form>
  );
};
